import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Star, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { formatINR } from '../utils/currency';

const BACKEND_URL = (typeof window !== 'undefined' && window.location.origin) || process.env.REACT_APP_BACKEND_URL;

// Detailed product information for skincare products
const PRODUCT_DETAILS = {
  'supershield-sunscreen': {
    fullName: 'Nuskay Supershield Sunscreen SPF 50 PA++++',
    tagline: 'An invisible veil of protection, perfected for everyday elegance.',
    description: 'Nuskay Supershield Sunscreen SPF 50 PA++++ is a lightweight, non-greasy daily sunscreen that offers broad-spectrum UVA and UVB protection while defending the skin from pollution. Enriched with Hyaluronic Acid and Licorice Extract, it hydrates, soothes, and helps prevent pigmentation and early signs of ageing. Ideal for everyday use and under makeup.',
    benefits: [
      'SPF 50 PA++++ broad-spectrum protection',
      'Protects against sun damage & pollution',
      'Lightweight, non-sticky & no white cast',
      'Hydrates and calms the skin',
      'Non-comedogenic & hypoallergenic',
      'Suitable for daily use & all skin types'
    ],
    howToUse: [
      'Apply generously on face and neck 20 minutes before sun exposure.',
      'Reapply every 2–3 hours or as needed.'
    ],
    ingredients: 'Aqua, Cocamidopropyl Betaine, Coco-Glucoside, Sodium Laureth Sulfate, Glycerin, Propanediol, Cocamide DEA, Niacinamide, Aqua (and) Aloe Barbadensis Leaf Extract, Glycerin (and) Aqua (and) Vaccinium Angustifolium (Blueberry) Fruit Extract, Olive Oil PEG-7 Esters, Polysorbate 20, Fragrance, Tocopheryl Acetate (Vitamin E), Salicylic Acid, Sodium Hyaluronate, Melaleuca Alternifolia (Tea Tree) Leaf Oil, Eucalyptus Oil, Potassium Sorbate, Sodium Gluconate, Sodium Benzoate.'
  },
  'pore-refining-cleanser': {
    fullName: 'Nuskay Pore Refining Cleanser',
    tagline: 'Gentle cleansing, visible clarity.',
    description: 'Nuskay Pore Refining Cleanser is a gentle yet effective daily face wash formulated to deeply cleanse pores, remove excess oil, and clear impurities without stripping the skin. Powered with encapsulated Salicylic Acid, it provides controlled exfoliation for smoother, clearer skin while maintaining hydration. Enriched with Vitamin E, Niacinamide, and soothing botanical extracts, it leaves the skin feeling fresh, balanced, and comfortable after every cleanse.',
    benefits: [
      'Deeply cleanses pores and removes excess oil',
      'Encapsulated Salicylic Acid for controlled exfoliation',
      'Maintains skin hydration while cleansing',
      'Enriched with Vitamin E and Niacinamide',
      'Leaves skin fresh, balanced, and comfortable',
      'Suitable for daily use'
    ],
    howToUse: [
      'Splash water onto your face.',
      'Pump a small amount of cleanser into your palm and gently massage onto wet skin using circular motions, avoiding the eye area.',
      'Rinse thoroughly with water and pat dry with a clean towel.',
      'Use twice daily for best results.'
    ],
    ingredients: 'Aqua, Cocamidopropyl Betaine, Coco-Glucoside, Sodium Laureth Sulfate, Glycerin, Propanediol, Cocamide DEA, Niacinamide, Aqua (and) Aloe Barbadensis Leaf Extract, Glycerin (and) Aqua (and) Vaccinium Angustifolium (Blueberry) Fruit Extract, Olive Oil PEG-7 Esters, Polysorbate 20, Fragrance, Tocopheryl Acetate (Vitamin E), Salicylic Acid, Sodium Hyaluronate, Melaleuca Alternifolia (Tea Tree) Leaf Oil, Eucalyptus Oil, Potassium Sorbate, Sodium Gluconate, Sodium Benzoate.'
  }
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [hoveredStar, setHoveredStar] = useState(0);

  // Fetch product details and reviews
  useEffect(() => {
    const fetchProductData = async () => {
      try {
        setLoading(true);
        const [productRes, reviewsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/products/${id}`),
          axios.get(`${BACKEND_URL}/api/products/${id}/reviews`)
        ]);
        setProduct(productRes.data);
        setReviews(reviewsRes.data);
      } catch (error) {
        console.error('Error fetching product data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProductData();
  }, [id]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (!user) {
      alert('Please login to submit a review');
      navigate('/login');
      return;
    }

    if (!reviewText.trim()) {
      alert('Please write a review');
      return;
    }

    try {
      setReviewLoading(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/products/${id}/reviews`,
        {
          rating,
          review_text: reviewText
        },
        { withCredentials: true }
      );

      // Add new review to the list
      setReviews([response.data, ...reviews]);
      setReviewText('');
      setRating(5);
      alert('Review submitted successfully!');
    } catch (error) {
      console.error('Error submitting review:', error);
      if (error.response?.status === 401) {
        alert('Please login to submit a review');
        navigate('/login');
      } else {
        alert(error.response?.data?.detail || 'Failed to submit review');
      }
    } finally {
      setReviewLoading(false);
    }
  };

  const addToCart = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      await axios.post(
        `${BACKEND_URL}/api/cart/add`,
        { product_id: id, quantity: 1 },
        { withCredentials: true }
      );
      alert('Added to cart!');
    } catch (error) {
      console.error('Error adding to cart:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
        navigate('/login');
      } else {
        alert('Failed to add to cart. Please try again.');
      }
    }
  };

  const calculateAverageRating = () => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return (sum / reviews.length).toFixed(1);
  };

  const renderStars = (currentRating, interactive = false, onHover = null, onClick = null) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-5 h-5 ${
              star <= currentRating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            } ${interactive ? 'cursor-pointer transition-colors' : ''}`}
            onMouseEnter={() => interactive && onHover && onHover(star)}
            onMouseLeave={() => interactive && onHover && onHover(0)}
            onClick={() => interactive && onClick && onClick(star)}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center">
        <p className="text-xl text-gray-600 mb-4">Product not found</p>
        <button
          onClick={() => navigate('/products')}
          className="text-purple-600 hover:text-purple-700 flex items-center"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Products
        </button>
      </div>
    );
  }

  // Get detailed info if available
  const productKey = product.name.toLowerCase().includes('supershield') 
    ? 'supershield-sunscreen'
    : product.name.toLowerCase().includes('pore refining')
    ? 'pore-refining-cleanser'
    : null;
  
  const detailedInfo = productKey ? PRODUCT_DETAILS[productKey] : null;
  const avgRating = calculateAverageRating();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/products')}
          className="flex items-center text-purple-600 hover:text-purple-700 mb-6 transition"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Products
        </button>

        {/* Product Details Section */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
          <div className="grid md:grid-cols-2 gap-8 p-6 md:p-8">
            {/* Product Image */}
            <div className="flex items-center justify-center bg-gray-100 rounded-lg overflow-hidden">
              {product.images && product.images.length > 0 ? (
                <img
                  src={product.images[0]}
                  alt={product.name}
                  className="w-full h-auto max-h-96 object-contain"
                />
              ) : (
                <div className="w-full h-96 flex items-center justify-center text-gray-400">
                  No Image Available
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="flex flex-col justify-between">
              <div>
                <h1 className="text-3xl font-bold mb-2">
                  {detailedInfo?.fullName || product.name}
                </h1>
                
                {/* Rating Display */}
                {reviews.length > 0 && (
                  <div className="flex items-center space-x-3 mb-4">
                    {renderStars(Math.round(avgRating))}
                    <span className="text-lg font-semibold">{avgRating}</span>
                    <span className="text-gray-600">({reviews.length} reviews)</span>
                  </div>
                )}

                {detailedInfo?.tagline && (
                  <p className="text-lg italic text-purple-600 mb-4">
                    {detailedInfo.tagline}
                  </p>
                )}

                <p className="text-gray-700 mb-6 leading-relaxed">
                  {detailedInfo?.description || product.description}
                </p>

                <div className="text-3xl font-bold text-purple-600 mb-6">
                  {formatINR(product.price)}
                </div>

                {product.stock > 0 ? (
                  <>
                    <button
                      onClick={addToCart}
                      className="w-full md:w-auto bg-purple-600 text-white px-8 py-3 rounded-lg hover:bg-purple-700 transition flex items-center justify-center space-x-2 mb-2"
                    >
                      <ShoppingCart className="w-5 h-5" />
                      <span>Add to Cart</span>
                    </button>
                    {product.stock < 10 && (
                      <p className="text-orange-600 text-sm">Only {product.stock} left in stock!</p>
                    )}
                  </>
                ) : (
                  <p className="text-red-600 text-lg font-semibold">Out of Stock</p>
                )}
              </div>
            </div>
          </div>

          {/* Detailed Information for Skincare Products */}
          {detailedInfo && (
            <div className="border-t border-gray-200 p-6 md:p-8 bg-gray-50">
              {/* Benefits */}
              {detailedInfo.benefits && (
                <div className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">Why you'll love it:</h2>
                  <ul className="space-y-2">
                    {detailedInfo.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-purple-600 mr-2">•</span>
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* How to Use */}
              {detailedInfo.howToUse && (
                <div className="mb-6">
                  <h2 className="text-2xl font-bold mb-4">How to Use:</h2>
                  <ol className="space-y-2">
                    {detailedInfo.howToUse.map((step, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-purple-600 font-semibold mr-2">{index + 1}.</span>
                        <span className="text-gray-700">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Ingredients */}
              {detailedInfo.ingredients && (
                <div>
                  <h2 className="text-2xl font-bold mb-4">Ingredients:</h2>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {detailedInfo.ingredients}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Reviews Section */}
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
          <h2 className="text-2xl font-bold mb-6">Customer Reviews</h2>

          {/* Review Submission Form */}
          {user ? (
            <form onSubmit={handleSubmitReview} className="mb-8 pb-8 border-b border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Write a Review</h3>
              
              {/* Star Rating Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Rating
                </label>
                {renderStars(
                  hoveredStar || rating,
                  true,
                  setHoveredStar,
                  setRating
                )}
              </div>

              {/* Review Text Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Review
                </label>
                <textarea
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  rows="4"
                  placeholder="Share your experience with this product..."
                  required
                />
              </div>

              <button
                type="submit"
                disabled={reviewLoading}
                className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition disabled:bg-gray-400"
              >
                {reviewLoading ? 'Submitting...' : 'Submit Review'}
              </button>
            </form>
          ) : (
            <div className="mb-8 pb-8 border-b border-gray-200 bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-600">
                Please{' '}
                <button
                  onClick={() => navigate('/login')}
                  className="text-purple-600 hover:text-purple-700 font-semibold"
                >
                  login
                </button>{' '}
                to write a review
              </p>
            </div>
          )}

          {/* Reviews List */}
          {reviews.length > 0 ? (
            <div className="space-y-6">
              {reviews.map((review) => (
                <div key={review.id} className="border-b border-gray-200 pb-6 last:border-b-0">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-gray-900">{review.user_name}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(review.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                    {renderStars(review.rating)}
                  </div>
                  <p className="text-gray-700 leading-relaxed">{review.review_text}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">
              No reviews yet. Be the first to review this product!
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
